import { tokenStore } from '../lib/tokenStore';

// API Client for making HTTP requests
class ApiClient {
  private baseURL: string;

  constructor(baseURL: string = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8787/api') {
    this.baseURL = baseURL;
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = tokenStore.getAccess();

    // Check if session expired (15 minutes)
    if (tokenStore.isExpired()) {
      tokenStore.clear();
      window.location.href = '/login';
      throw new Error('Session expired');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token expired, clear memory store and redirect
        tokenStore.clear();
        window.location.href = '/login';
      }
      // Try to get error message from response body
      let errorMessage = response.statusText || `HTTP ${response.status}`;
      try {
        const errorBody = await response.json();
        // Handle various error response formats
        // Check message first (Spring's default error format uses message for the actual error)
        if (errorBody.message && errorBody.message !== '') {
          errorMessage = errorBody.message;
        } else if (errorBody.error && typeof errorBody.error === 'string' && errorBody.error !== 'Bad Request') {
          errorMessage = errorBody.error;
        } else if (errorBody.error?.message) {
          errorMessage = errorBody.error.message;
        } else if (typeof errorBody === 'string') {
          errorMessage = errorBody;
        }
      } catch {
        // Ignore JSON parse errors
      }
      throw new Error(errorMessage);
    }

    // Handle empty responses
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    return {} as T;
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

const apiClient = new ApiClient();
export default apiClient;

